import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Loader2, MapPin, Building2, ChevronRight, Inbox } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { directoryApi } from "@/lib/api/directory";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

const TYPE_LABELS = {
    CLINIC: { en: "Clinic", ar: "عيادة" },
    HOSPITAL: { en: "Hospital", ar: "مستشفى" },
    PHARMACY: { en: "Pharmacy", ar: "صيدلية" },
    LAB: { en: "Lab", ar: "معمل" },
    RADIOLOGY_CENTER: { en: "Radiology", ar: "أشعة" },
    OTHER: { en: "Other", ar: "أخرى" },
};

function ProviderCard({ provider, lang }) {
    const description = lang === "ar"
        ? (provider.description_ar || provider.description_en)
        : (provider.description_en || provider.description_ar);
    const typeLabel = TYPE_LABELS[provider.type]?.[lang] || provider.type;
    return (
        <Link
            to={`/member/provider/${provider.id}`}
            className="group surface-card p-5 hover:border-primary/40 hover:shadow-md transition-all flex flex-col gap-3"
            data-testid={`directory-provider-${provider.id}`}
        >
            <div className="flex items-start gap-3">
                <div className="icon-tile h-12 w-12 shrink-0">
                    <Building2 className="h-6 w-6" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold truncate">{provider.corporate_name}</h3>
                    <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant="outline" className="text-[10px]">{typeLabel}</Badge>
                        <span>{provider.branch_count} {lang === "ar" ? "فرع" : "branch(es)"}</span>
                    </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground mt-1" />
            </div>
            {description && (
                <p className="text-sm text-muted-foreground line-clamp-2">{description}</p>
            )}
            {provider.neighborhoods?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 items-center text-xs text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" />
                    {provider.neighborhoods.slice(0, 4).map((n) => (
                        <span key={n} className="px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
                            {n}
                        </span>
                    ))}
                </div>
            )}
        </Link>
    );
}

export default function ProviderListingPage() {
    const { t, lang } = useI18n();
    const { l2Id } = useParams();
    const [data, setData] = useState(null);

    useEffect(() => {
        let active = true;
        directoryApi.providersForCategory(l2Id)
            .then((d) => { if (active) setData(d); })
            .catch((err) => active && toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message));
        return () => { active = false; };
    }, [l2Id]);

    if (!data) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]" data-testid="page-member-providers-loading">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
        );
    }

    const cat = data.category;
    const catName = lang === "ar" ? cat.name_ar : cat.name_en;
    const backTo = (cat.parent_ids && cat.parent_ids[0]) ? `/member/category/${cat.parent_ids[0]}` : "/member/home";

    return (
        <div className="max-w-6xl space-y-6 fade-up" data-testid="page-member-providers">
            <Link
                to={backTo}
                className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
                data-testid="back-to-category"
            >
                <ChevronLeft className="h-4 w-4 me-1" />
                {t("common.back")}
            </Link>

            <header>
                <p className="text-sm font-medium text-primary">{t("member.providers.eyebrow")}</p>
                <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight">{catName}</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                    {t("member.providers.count", { n: data.items.length })}
                </p>
            </header>

            {data.items.length === 0 ? (
                <div className="surface-card p-10 text-center" data-testid="providers-empty">
                    <Inbox className="mx-auto h-8 w-8 text-muted-foreground mb-3" />
                    <p className="text-sm font-medium text-foreground">
                        {t("member.providers.emptyTitle")}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                        {t("member.providers.emptyHint")}
                    </p>
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                    {data.items.map((p) => (
                        <ProviderCard key={p.id} provider={p} lang={lang} />
                    ))}
                </div>
            )}
        </div>
    );
}
